import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'

import { Box, makeStyles } from '@material-ui/core'

import AccountItem from './AccountItem'
import ClientData from './ClientData'
import CustomDivider from './CustomDivider'
import { getSelectedItemIdsFromRoute, ITEM_SEPARATOR } from './CustomerSidebar.helpers'
import useBrandStyles from './CustomerSidebarBrandStyles'
import ListItem from './ListItem'

import { Customer } from '../../model/customer.model'

import { Brand, useBrand } from '~/brand'
import { selectFeatureFlag, Flags } from '~/featureFlags'
import { PATHS } from '~/pages/router/paths'
import { getIdByParam } from '~/pages/router/router.helpers'
import Permission from '~/permissions/model/permissions.model'
import { selectHasPermission } from '~/permissions/store/permissions.selectors'

interface CustomerSidebarProps {
  customer: Customer
}

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    paddingTop: theme.spacing(3),
    color: theme.palette.secondary.contrastText,
    overflow: 'visible',
    width: 'inherit',
    height: 'inherit',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  listTriangleGap: {
    width: `calc(100% + ${theme.spacing(5)}px)`,
  },
  listTriangleGapCompensor: {
    width: `calc(100% - ${theme.spacing(5)}px)`,
  },
  scrollBarWrapper: {
    height: '100%',
    width: 'calc(100% + 15px)',
    overflow: 'hidden',
    position: 'relative',
  },
  scrollBarContent: {
    position: 'absolute',
    top: '0',
    bottom: '0',
    left: '0',
    right: `-${theme.spacing(5)}px`,
    overflowY: 'scroll',
  },
}))

const CustomerSidebar: React.FC<CustomerSidebarProps> = ({ customer }) => {
  const brandClasses = useBrandStyles()
  const classes = useStyles()
  const location = useLocation()
  const history = useHistory()
  const { brand } = useBrand()
  const isEnergyContractsActiveFlag = useSelector(selectFeatureFlag(Flags.isEnergyContractsActive))

  const hasPermissionViewEnergyContracts = useSelector(
    selectHasPermission(Permission.VIEW_ENERGY_CONTRACTS),
  )

  const selectedItemsIds = getSelectedItemIdsFromRoute(location.pathname, customer)

  const createUriFromPathId = (pathId: string) => {
    if (
      pathId.includes(PATHS.OFFERS_CONFIGURATOR) ||
      pathId.includes(PATHS.PRODUCT) ||
      pathId.includes(PATHS.ENERGY) ||
      pathId.includes(PATHS.CUSTOMER)
    ) {
      return `/${pathId
        .replace(new RegExp(`${ITEM_SEPARATOR}`, 'g'), '/')
        .replace(/account\/\d+\//, '')}`
    }

    return ''
  }

  const onChangeSelected = (pathId: string): void => {
    const targetURI = createUriFromPathId(String(pathId))

    if (targetURI) {
      history.push({
        pathname: targetURI,
        search: `?brand=${brand}`,
      })
    }
  }

  const customerPathId = `${PATHS.CUSTOMER}${ITEM_SEPARATOR}${getIdByParam(
    'customer',
    location.pathname,
  )}`

  return (
    <Box className={`${brandClasses.root} ${classes.mainWrapper}`} data-hook="customer-side-bar">
      <Box className={`${classes.listTriangleGap}`}>
        <ClientData customer={customer} />
        <ListItem
          dataHook="client-zone"
          text="Zona cliente"
          onChangeSelected={onChangeSelected}
          itemId={customerPathId}
          selectedItemsIds={selectedItemsIds}
        />
        <CustomDivider />
        {isEnergyContractsActiveFlag &&
          brand === Brand.masmovil &&
          hasPermissionViewEnergyContracts && (
            <>
              <ListItem
                dataHook="energy-contract"
                text="Contrato de Energía"
                onChangeSelected={onChangeSelected}
                itemId={`${customerPathId}/${PATHS.ENERGY}`}
                selectedItemsIds={selectedItemsIds}
              />
              <CustomDivider />
            </>
          )}
      </Box>
      <Box
        overflow="auto"
        height="100%"
        className={`${classes.listTriangleGap}  ${classes.scrollBarWrapper}`}
      >
        <div className={classes.scrollBarContent}>
          {customer.accounts?.map((account) => (
            <AccountItem
              key={account.id}
              account={account}
              onChangeSelected={onChangeSelected}
              selectedItemsIds={selectedItemsIds}
              itemId={`${customerPathId}/${PATHS.ACCOUNT}${ITEM_SEPARATOR}${account.id}`}
            />
          ))}
        </div>
      </Box>
    </Box>
  )
}

export default CustomerSidebar
